import {
  Autocomplete,
  FormControl,
  FormHelperText,
  SelectProps,
  Skeleton,
  TextField,
} from '@mui/material';
import { JSX } from 'react';
import { Option } from '../../types/option';

export interface FormSelectProps extends Pick<SelectProps, 'label' | 'error' | 'value'> {
  /**
   * Callback that is called when the value of the select changes.
   */
  onChange: (value: unknown) => void;
  /**
   * The key of the option object that should be used as the value.
   * @default 'id'
   */
  valueKey?: keyof Option;
  /**
   * The options to display in the select.
   * If not provided, a loading state will be displayed.
   */
  options?: Partial<{ id: string | number; name: string; note: string }>[];
  /**
   * Helper text to display below the select.
   */
  helperText?: string;
}

/**
 * Base select component for forms.
 */
const FormSelect = ({
  label,
  error,
  options,
  onChange,
  helperText,
  valueKey = 'id',
  ...props
}: FormSelectProps): JSX.Element => {
  return (
    <FormControl fullWidth error={error}>
      {options ? (
        <Autocomplete
          {...props}
          disableClearable
          options={options}
          value={options.find((option) => option[valueKey] === props.value)}
          onChange={(_, newValue): void => onChange(newValue?.[valueKey])}
          getOptionLabel={(option): string => option.note ?? ''}
          renderInput={(parameters): JSX.Element => (
            <TextField {...parameters} error={error} size="small" label={label} />
          )}
        />
      ) : (
        <Skeleton variant="rounded" className="mt-7" height={36} />
      )}
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};

export default FormSelect;
